import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Users, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, fmtErr } from "../lib/api";

export default function EmbedBooking() {
  const { rid } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [error, setError] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [party, setParty] = useState("2");
  const [slots, setSlots] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", dietary: "", notes: "", occasion: "" });
  const [answers, setAnswers] = useState({});
  const [formError, setFormError] = useState("");
  const [confirmed, setConfirmed] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    document.documentElement.classList.remove("dark");
    api.get(`/restaurants/${rid}`)
      .then((r) => setRestaurant(r.data))
      .catch(() => setError("Booking is unavailable"));
  }, [rid]);

  useEffect(() => {
    if (window.parent === window) return;
    const post = () => window.parent.postMessage({ type: "tablediary:height", height: document.body.scrollHeight }, "*");
    post();
    const ro = new ResizeObserver(post);
    ro.observe(document.body);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    if (window.parent !== window) {
      window.parent.postMessage({ type: "tablediary:height", height: document.body.scrollHeight }, "*");
    }
  }, [step, slots, confirmed]);

  const checkAvailability = async () => {
    setSelectedSlot(null);
    try {
      const { data } = await api.get(`/restaurants/${restaurant.id}/availability`, { params: { date, party_size: Number(party) } });
      setSlots(data.slots);
    } catch (e) {
      alert(fmtErr(e));
    }
  };

  const book = async () => {
    const qs = restaurant.custom_questions || [];
    for (const q of qs) {
      if (q.required && !(answers[q.id] || "").trim()) { setFormError(`Please answer: ${q.label}`); return; }
    }
    setFormError("");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/restaurants/${restaurant.id}/bookings`, {
        ...form, date, time: selectedSlot, party_size: Number(party), source: "widget",
        answers: Object.entries(answers).map(([id, answer]) => ({ id, answer })),
      });
      if (data.checkout_url) {
        window.top.location.href = data.checkout_url;
        return;
      }
      setConfirmed(data);
    } catch (e) {
      alert(fmtErr(e));
    }
    setSubmitting(false);
  };

  if (error) return <div className="p-8 text-center text-sm text-zinc-500 bg-white min-h-screen">{error}</div>;
  if (!restaurant) return <div className="bg-white min-h-screen" />;

  const maxParty = restaurant.booking_policy?.max_party_size || 12;
  const cfg = restaurant.widget_form || {};
  const reqMark = (on) => (on ? " *" : " (optional)");
  const missingRequired = !form.phone.trim()
    || (cfg.require_dietary && !form.dietary.trim())
    || (cfg.require_notes && !form.notes.trim());
  const missingQuestions = (restaurant.custom_questions || []).some((q) => q.required && !(answers[q.id] || "").trim());
  const missingAny = missingRequired || missingQuestions;

  return (
    <div data-testid="embed-booking" className="bg-white text-zinc-900 min-h-screen p-4 font-sans">
      <div className="max-w-md mx-auto rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
        {confirmed ? (
          <div className="text-center py-4" data-testid="embed-confirmation">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
            <h2 className="font-semibold text-xl mt-3">You're booked</h2>
            <p className="text-sm text-zinc-500 mt-2">
              {restaurant.name} · {confirmed.date} at {confirmed.time}<br />
              Party of {confirmed.party_size} · Ref: {confirmed.id.slice(0, 8)}
            </p>
            <p className="text-xs text-zinc-400 mt-3">Confirmation sent to {confirmed.email}</p>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <h2 className="font-semibold text-xl">Book a table at {restaurant.name}</h2>
              <span className="text-[11px] text-zinc-400 shrink-0 mt-1" data-testid="embed-step-indicator">Step {step} of 2</span>
            </div>
            {step === 1 && (<>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <Input data-testid="embed-date" type="date" value={date} min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => { setDate(e.target.value); setSlots(null); setSelectedSlot(null); setStep(1); }}
                className="bg-white border-zinc-300 text-zinc-900 w-full min-w-0" />
              <Select value={party} onValueChange={(v) => { setParty(v); setSlots(null); setSelectedSlot(null); setStep(1); }}>
                <SelectTrigger data-testid="embed-party" className="bg-white border-zinc-300 text-zinc-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: maxParty }, (_, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{i + 1} {i === 0 ? "guest" : "guests"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="embed-check-btn" onClick={checkAvailability} variant="secondary"
              className="w-full mt-3 bg-zinc-100 hover:bg-zinc-200 text-zinc-900">
              <Users className="w-4 h-4 mr-2" /> Check availability
            </Button>
            {slots && (
              <div className="mt-4" data-testid="embed-slots">
                {slots.length === 0 ? (
                  <p className="text-sm text-zinc-500 text-center py-2">No availability — try another date.</p>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-72 overflow-y-auto">
                    {slots.map((s) => (
                      <button key={s.time} data-testid={`embed-slot-${s.time}`} onClick={() => { setSelectedSlot(s.time); setStep(2); }}
                        className={`rounded-lg border px-2 py-2 text-sm transition-colors ${
                          selectedSlot === s.time ? "bg-[#C4572E] text-white border-[#C4572E]" : "border-zinc-300 hover:border-[#C4572E]/60"
                        }`}>
                        {s.time}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {!slots && (
              <p className="text-xs text-zinc-400 mt-5 text-center" data-testid="embed-step1-hint">Pick a date and party size, check availability, then tap a time.</p>
            )}
            </>)}
            {step === 2 && (
              <div className="mt-4 space-y-2.5">
                <button type="button" data-testid="embed-back-btn" onClick={() => setStep(1)}
                  className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors">
                  ← {date} · {selectedSlot} · party of {party} — change
                </button>
                <Input data-testid="embed-name" placeholder="Full name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-zinc-300 text-zinc-900" />
                <Input data-testid="embed-email" type="email" placeholder="Email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white border-zinc-300 text-zinc-900" />
                <Input data-testid="embed-phone" placeholder="Phone *" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white border-zinc-300 text-zinc-900" />
                <Input data-testid="embed-dietary" placeholder={`Dietary / allergies${reqMark(cfg.require_dietary)}`} value={form.dietary}
                  onChange={(e) => setForm({ ...form, dietary: e.target.value })} className="bg-white border-zinc-300 text-zinc-900" />
                <Input data-testid="embed-notes" placeholder={`Notes / special requests${reqMark(cfg.require_notes)}`} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-white border-zinc-300 text-zinc-900" />
                {cfg.show_occasion !== false && (
                  <Select value={form.occasion} onValueChange={(v) => setForm({ ...form, occasion: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="embed-occasion" className="bg-white border-zinc-300 text-zinc-900">
                      <SelectValue placeholder="Occasion (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No occasion</SelectItem>
                      {["Birthday", "Anniversary", "Date night", "Business meal", "Celebration", "Other"].map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {(restaurant.custom_questions || []).map((q) => (
                  <div key={q.id}>
                    {q.type === "yesno" || q.type === "choice" ? (
                      <Select value={answers[q.id] || ""} onValueChange={(v) => setAnswers({ ...answers, [q.id]: v })}>
                        <SelectTrigger data-testid={`embed-q-${q.id}`} className="bg-white border-zinc-300 text-zinc-900">
                          <SelectValue placeholder={`${q.label}${q.required ? " *" : ""}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {(q.type === "yesno" ? ["Yes", "No"] : q.options).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input data-testid={`embed-q-${q.id}`} placeholder={`${q.label}${q.required ? " *" : " (optional)"}`}
                        value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                        className="bg-white border-zinc-300 text-zinc-900" />
                    )}
                  </div>
                ))}
                {missingAny && (
                  <p className="text-xs text-amber-600" data-testid="embed-required-hint">Fields marked * are required by this restaurant.</p>
                )}
                {formError && (
                  <p className="text-xs text-red-600" data-testid="embed-form-error">{formError}</p>
                )}
                <Button data-testid="embed-confirm-btn" onClick={book} disabled={submitting || !form.name || !form.email || missingAny}
                  className="w-full bg-[#C4572E] hover:bg-[#a84823] text-white">
                  {submitting ? "Booking..." : `Confirm ${selectedSlot} · party of ${party}`}
                </Button>
              </div>
            )}
          </>
        )}
        <p className="text-[10px] text-zinc-400 text-center mt-4">Powered by TableDiary</p>
      </div>
    </div>
  );
}
